import {
  Box,
  Skeleton,
  Stack,
  TableBody,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";
import Table from "@mui/material/Table";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import React from "react";
import { useAuth } from "../../sdk";
import { Page } from "../Styles";
import { currencies } from "../../sdk/codeConstant";
import { ComparisonTableResponsive } from "./ComparisontableResponsive";

const ClassWrapper = styled(Box)(() => ({
  ".scrollContainer": {
    overflow: "auto",
    maxHeight: "70vh",
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#163A90",
      borderRadius: "6px",
    },
  },
  ".MuiTableRow-head": {
    backgroundColor: "#306fbc",
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#306fbc",
    color: "white",
    fontSize: "14px",
    // border: 0,
  },
}));

const ComparisonTable = ({
  channels,
  tableData,
  isLoading,
  showSkeleton,
  isMobileScreen,
}) => {
  const { currentHotel } = useAuth();
  const brandWebsite = channels?.find((channel) => channel?.isBrandWebsite);
  const brandWebsiteSourceId = channels?.find((item) => item?.isBrandWebsite)
    ?.sourceId;

  const nonBrandWebsites = channels?.filter(
    (channel) => !channel?.isBrandWebsite,
  );
  const sortedChannels = brandWebsite
    ? [brandWebsite, ...nonBrandWebsites]
    : nonBrandWebsites;
  channels = sortedChannels;

  const handlePriceColor = (row, sourceId) => {
    const isBrandWebsite = brandWebsiteSourceId === sourceId;

    const sources = row?.hotelResult?.sourceSortedArray || [];

    const brandWebsite = sources.find(
      (item) => brandWebsiteSourceId === item?.sourceId,
    );

    const brandWebsitePrice =
      brandWebsite?.internalStatus === "available"
        ? brandWebsite?.price
        : undefined;

    const otaSources = sources.filter(
      (item) =>
        item?.internalStatus === "available" &&
        item?.price != null &&
        brandWebsiteSourceId !== item?.sourceId,
    );

    const otaPrices = otaSources.map((item) => item?.price);

    const target = sources.find((item) => item?.sourceId === sourceId);

    // Sold Out → Red
    if (target?.internalStatus === "sold_out") return "red";

    // Brand Website lower than all OTAs
    if (isBrandWebsite && brandWebsitePrice !== undefined) {
      if (
        otaPrices?.length > 0 &&
        otaPrices.every((p) => brandWebsitePrice <= p)
      ) {
        return "red";
      }
    }

    // OTA lower than Brand Website
    if (
      !isBrandWebsite &&
      brandWebsitePrice !== undefined &&
      target?.internalStatus === "available" &&
      target?.price < brandWebsitePrice
    ) {
      return "green";
    }

    // Only OTAs can be Blue (highest)
    if (!isBrandWebsite) {
      const availablePrices = sources
        .filter(
          (item) => item?.internalStatus === "available" && item?.price != null,
        )
        .map((item) => item.price);

      const maxPrice =
        availablePrices.length > 0 ? Math.max(...availablePrices) : null;

      const isAllOtaMaxPriceSame =
        otaPrices.length > 1 && otaPrices.every((item) => maxPrice === item);

      if (
        target?.internalStatus === "available" &&
        target?.price === maxPrice &&
        !isAllOtaMaxPriceSame &&
        brandWebsitePrice !== undefined &&
        target?.price > brandWebsitePrice
      ) {
        return "blue";
      }
    }

    // Default → black
    return "inherit";
  };

  const isAnyColumnRed = (row) => {
    const sources = row?.hotelResult?.sourceSortedArray || [];

    return sources.some((source) => {
      const color = handlePriceColor(row, source?.sourceId);
      return color === "red";
    });
  };

  if (isMobileScreen)
    return (
      <ComparisonTableResponsive
        isLoading={isLoading}
        tableData={tableData}
        currentHotel={currentHotel}
        channels={channels}
        currencies={currencies}
      />
    );

  return (
    <Page>
      <ClassWrapper>
        {isLoading && showSkeleton ? (
          [...Array(7)].map((_, index) => (
            <Skeleton
              key={index}
              variant="rounded"
              height={40}
              width="90%"
              sx={{ mt: 5 }}
            />
          ))
        ) : (
          <Box
            sx={{
              width: "90%",
              borderRadius: "5px",
            }}
          >
            <div className="scrollContainer">
              <Table
                stickyHeader
                sx={{
                  border: "0.5px solid #ABABAB",
                }}
                size="small"
              >
                <TableHead>
                  <TableRow
                    sx={{
                      height: "62px",
                    }}
                  >
                    <StyledTableCell
                      align="center"
                      sx={{
                        borderRight: "1px solid #ABABAB",
                      }}
                    >
                      Date
                    </StyledTableCell>

                    {currentHotel?.viewChannelManagerPrice && (
                      <StyledTableCell align="center">
                        Channel Manager
                      </StyledTableCell>
                    )}
                    {channels?.map((channel) => {
                      if (channel?.isBrandWebsite) {
                        return (
                          <StyledTableCell align="center" key={channel?.id}>
                            Brand Website
                          </StyledTableCell>
                        );
                      } else {
                        return (
                          <StyledTableCell align="center" key={channel?.id}>
                            {channel?.name}
                          </StyledTableCell>
                        );
                      }
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableData.map((row) => {
                    return (
                      <StyledTableRow
                        key={row.rateDate}
                        sx={{
                          height: "48px",
                          background: row.index === 0 ? "#DFDFDF" : "",
                        }}
                      >
                        <TableCell
                          align="center"
                          component="th"
                          scope="row"
                          sx={{
                            width: "250px",
                            fontSize: "14px",
                            fontWeight: 600,
                            borderRight: "1px solid #ABABAB",
                          }}
                        >
                          <Stack
                            direction="row"
                            justifyContent="center"
                            alignItems="center"
                            gap="16px"
                          >
                            {row.rateDate}
                            {row?.isSearching && (
                              <StyledSearching
                                component="img"
                                src="/assets/loading.svg"
                                alt="refreshing"
                                title="Processing"
                              />
                            )}
                          </Stack>
                        </TableCell>

                        {currentHotel?.viewChannelManagerPrice && (
                          <TableCell align="center">
                            <Typography
                              fontSize="14px"
                              color={isAnyColumnRed(row) ? "red" : "inherit"}
                            >
                              {row.channelManagerPrice !== null &&
                              row.channelManagerPrice !== 0
                                ? (currencies?.[currentHotel.RSCurrency]
                                    ?.symbol ?? currentHotel.RSCurrency) +
                                  row.channelManagerPrice
                                : "--"}
                            </Typography>
                          </TableCell>
                        )}

                        {channels
                          ?.map(
                            ({ sourceId }) =>
                              row?.hotelResult?.sourceSortedArray?.find(
                                (channel) => channel?.sourceId === sourceId,
                              ) ?? { price: null },
                          )
                          .map((source) => {
                            const sourceURL = source?.sourceURL;
                            return (
                              <TableCell align="center" key={source?.sourceId}>
                                <Typography
                                  fontSize="14px"
                                  color={handlePriceColor(
                                    row,
                                    source?.sourceId,
                                  )}
                                  sx={{
                                    cursor: sourceURL ? "pointer" : "default",
                                    ":hover": {
                                      textDecoration: sourceURL
                                        ? "underline"
                                        : "none",
                                    },
                                  }}
                                  onClick={() =>
                                    sourceURL &&
                                    window.open(
                                      sourceURL,
                                      "_blank",
                                      "noopener,noreferrer",
                                    )
                                  }
                                >
                                  {source &&
                                  source?.internalStatus === "available"
                                    ? (currencies?.[currentHotel.RSCurrency]
                                        ?.symbol ?? currentHotel.RSCurrency) +
                                      source?.price
                                    : null}
                                  {source &&
                                  source?.internalStatus === "sold_out"
                                    ? "Sold Out"
                                    : null}
                                  {source &&
                                  source?.internalStatus === "error" ? (
                                    <Tooltip
                                      color="error"
                                      sx={{
                                        fontSize: "20px",
                                      }}
                                      title={source.errMsg}
                                    >
                                      <ReportProblemIcon />
                                    </Tooltip>
                                  ) : null}
                                  {!source.price && source?.price !== 0
                                    ? "--"
                                    : null}
                                </Typography>
                              </TableCell>
                            );
                          })}
                      </StyledTableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </Box>
        )}
      </ClassWrapper>
    </Page>
  );
};

export default ComparisonTable;

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "rgba(116, 116, 116, 0.2)",
  },
  "& td": {
    border: 0,
    fontSize: "14px",
  },
}));

const StyledSearching = styled(Box)(() => ({
  height: "30px",
  aspectRatio: "1/1",
  animation: "spin 1s infinite linear",

  "@keyframes spin": {
    from: {
      transform: "rotate(0deg)",
    },
    to: {
      transform: "rotate(360deg)",
    },
  },
}));
